import { fetchRequestWrapper } from "@/lib/utilities";
import { setButtonBackToActive } from "./tradeDataStoreACTIONS";
import { generateToastMessageTradeData } from "@/lib/notifications/toastNotificationHelpers";
import { IOrderFill } from "@/lib/interfaces and Enums/InterfaceExecutionRequestQuoteLeg";
import tradeDataStore, { IFillToFlash } from "./tradeDataStore";
import { toast } from "react-toastify";
import { isFillCancelled } from "@/components/widgets/tradeActionSummary/helper/fillStateHelpers";
import { containsHyphenInQuote, returnFractionString } from "@/lib/businessRules/dollarFractionBusinessRule";
import {
  hasLeadingMinus,
  isNumericallyZero,
} from "@/lib/businessRules/zeroNormalizationHelper";
import { isPriceNotation } from "@/lib/globalFormatterHelper";

export interface IworkingObject {
  esid: string;
  executionRequestId: number;
}

export async function workingStoreAction({
  data,
  getAccessTokenSilently,
}: {
  data: any;
  getAccessTokenSilently: Function;
}) {
  const outgoingTradeMessage: IworkingObject = {
    esid: data.esid,
    executionRequestId: data.executionRequestId,
  };
  tradeDataStore.getState().updateEsidsToExpand(data.esid);

  const workingOrderResponse = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/Working",
      method: "PUT",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );
  if (workingOrderResponse) {
    generateToastMessageTradeData({
      action: "workingStoreAction",
      result: "success",
      ESIDPassed: data.esid,
    });
  }
  setButtonBackToActive(outgoingTradeMessage.esid);
}

export interface IeditFilledObject {
  esid: string;
  executionRequestId: number;
}

export async function editFilledStoreAction({
  data,
  getAccessTokenSilently,
}: {
  data: any;
  getAccessTokenSilently: Function;
}) {
  const outgoingTradeMessage: IeditFilledObject = {
    esid: data.esid,
    executionRequestId: data.executionRequestId,
  };

  const editFilledResponse = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/EditFill",
      method: "PUT",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );
  if (editFilledResponse) {
    generateToastMessageTradeData({
      action: "editFilledStoreAction",
      result: "success",
      ESIDPassed: data.esid,
    });
  }
  setButtonBackToActive(outgoingTradeMessage.esid);
}

// --- UPDATE FILLS ---

export interface IUpdateFillsObject {
  esid: string;
  executionRequestId: number;
  blocks: IBlockPayload[];
}

export interface IBlockPayload {
  blockId: number;
  fills: IFillPayload[];
  bidTransactionFee?: number;
  askTransactionFee?: number;
  midTransactionFee?: number;
}

export interface IFillPayload {
  fillId: number | string;
  fillType: "New" | "Update" | "Cancel" | "Unchanged";
  fillSize?: number;
  fillRate?: string | number;
  fillRateFraction?: string;
  ExtFillId?: string;
}

/**
 * Maps the IOrderFill.fillType field to the API payload fillType.
 * - "New" -> "New"
 * - "Updated" -> "Update"
 * - "Cancel" -> "Cancel"
 * - undefined or "Unchanged" -> "Unchanged" (fills from backend with no edits)
 */
export function mapFillTypeForPayload(
  fillType: IOrderFill["fillType"],
): IFillPayload["fillType"] {
  if (fillType === "New") {
    return "New";
  }
  if (fillType === "Updated") {
    return "Update";
  }
  if (fillType === "Cancel") {
    return "Cancel";
  }
  // undefined or "Unchanged" - fill came from backend and wasn't edited
  return "Unchanged";
}

/**
 * Determines the fillId to send based on the fillType field.
 * - For Update, Cancel, or Unchanged: send the actual fillId
 * - For New fills: send 0 (backend will assign the ID)
 */
export function getFillIdForPayload(fill: IOrderFill): number | string {
  const fillType = mapFillTypeForPayload(fill.fillType);
  if (fillType === "New") {
    return 0;
  }
  // For Update, Cancel, or Unchanged - send the actual fillId
  return fill.fillId;
}

/**
 * Validates that all fills have a price.
 * Returns an array of fills that are missing prices.
 */
export function validateFillsHavePrices(data: any): IFillToFlash[] {
  const fillsMissingPrices: IFillToFlash[] = [];

  if (!data.legs) {
    return fillsMissingPrices;
  }

  data.legs.forEach((leg: any) => {
    if (leg.fills && Array.isArray(leg.fills)) {
      leg.fills.forEach((fill: IOrderFill) => {
        // Skip cancelled fills - they don't need validation
        if (isFillCancelled(fill)) {
          return;
        }
        if (fill.fillRate === undefined || fill.fillRate === null) {
          fillsMissingPrices.push({
            esid: data.esid,
            blockId: fill.blockId ?? leg.blockId,
            fillId: fill.fillId,
          });
        }
      });
    }
  });

  return fillsMissingPrices;
}

/**
 * Iterates fills on $p / $fr legs and returns those whose fillRate matches the predicate.
 * Mirrors the quote-side zero/negative validators in quoteFlashHelper so Orders get
 * the same treatment as RFQs for price-notation legs.
 */
function collectFillsMatching(
  data: any,
  predicate: (fillRate: unknown) => boolean,
): IFillToFlash[] {
  const matches: IFillToFlash[] = [];
  if (!data.legs) return matches;

  data.legs.forEach((leg: any) => {
    if (!isPriceNotation(leg.notation)) return;
    if (!leg.fills || !Array.isArray(leg.fills)) return;

    leg.fills.forEach((fill: IOrderFill) => {
      if (isFillCancelled(fill)) return;
      if (fill.fillRate == null) return;
      if (predicate(fill.fillRate)) {
        matches.push({
          esid: data.esid,
          blockId: fill.blockId ?? leg.blockId,
          fillId: fill.fillId,
        });
      }
    });
  });

  return matches;
}

/**
 * $p / $fr fills cannot be zero. Catches every zero form (0, 00, 0.00, 0-00, -0, -0-00 …)
 * via the shared isNumericallyZero regex.
 */
export function validateFillsNotZeroOnPriceNotation(data: any): IFillToFlash[] {
  return collectFillsMatching(data, (rate) => isNumericallyZero(rate));
}

/**
 * $p / $fr fills cannot be negative. -0 forms are treated as zero (caught separately),
 * not negative — matches the quote-side rule in quoteFlashHelper.
 */
export function validateFillsNotNegativeOnPriceNotation(data: any): IFillToFlash[] {
  return collectFillsMatching(
    data,
    (rate) => hasLeadingMinus(rate) && !isNumericallyZero(rate),
  );
}

/**
 * Builds the blocks array payload from the trade's legs data.
 * Groups fills by blockId and formats them for the API.
 */
export function buildBlocksPayload(data: any): IBlockPayload[] {
  const blocks: IBlockPayload[] = [];

  if (!data.legs) {
    return blocks;
  }

  data.legs.forEach((leg: any) => {
    const fills: IFillPayload[] = [];

    if (leg.fills && Array.isArray(leg.fills)) {
      let autoExtFillId = 1;
      leg.fills.forEach((fill: IOrderFill) => {
        const fillType = mapFillTypeForPayload(fill.fillType);
        const fillPayload: IFillPayload = {
          fillId: getFillIdForPayload(fill),
          fillType: fillType,
        };

        // Only include fillRate if it's a valid value
        if (fill.fillRate !== undefined && fill.fillRate !== "") {
          // $fr: fraction strings (e.g., "99-16") are sent as-is
          if (typeof fill.fillRate === "string" && containsHyphenInQuote(fill.fillRate)) {
            // $fr: split into whole + fraction (e.g., "90-12" → fillRate: "90", fillRateFraction: "12")
            fillPayload.fillRate = returnFractionString(fill.fillRate, true);
            fillPayload.fillRateFraction = returnFractionString(fill.fillRate, false);
          } else {
            const parsedRate = parseFloat(fill.fillRate);
            if (!isNaN(parsedRate)) {
              fillPayload.fillRate = fill.fillRate;
            }
          }
        }
        if (fill.fillSize !== undefined) {
          fillPayload.fillSize = fill.fillSize;
        }
        // Always send ExtFillId to backend for stable ordering
        // Use existing extFillId if available, otherwise assign based on current position
        if (fill.extFillId != null) {
          fillPayload.ExtFillId = fill.extFillId.toString();
        } else {
          fillPayload.ExtFillId = String(autoExtFillId);
        }
        autoExtFillId++;

        fills.push(fillPayload);
      });
    }

    // Only add block if it has fills
    if (fills.length > 0) {
      const blockPayload: IBlockPayload = {
        blockId: leg.blockId,
        fills: fills,
        // bidTransactionFee: leg.bidTransactionFee, // Are these needed?
        // askTransactionFee: leg.askTransactionFee, // Are these needed?
        // midTransactionFee: leg.midTransactionFee, // Are these needed?
      };

      // Add transaction fees if they exist on the leg
      // if (leg.bidTransactionFee !== undefined) {
      //   blockPayload.bidTransactionFee = Number(leg.bidTransactionFee);
      // }
      // if (leg.askTransactionFee !== undefined) {
      //   blockPayload.askTransactionFee = Number(leg.askTransactionFee);
      // }
      // if (leg.midTransactionFee !== undefined) {
      //   blockPayload.midTransactionFee = Number(leg.midTransactionFee);
      // }

      blocks.push(blockPayload);
    }
  });

  return blocks;
}

export interface INoMoreFillsObject {
  esid: string;
  executionRequestId: number;
  blocks: [];
}

export async function noMoreFillsStoreAction({
  data,
  getAccessTokenSilently,
}: {
  data: any;
  getAccessTokenSilently: Function;
}) {
  const outgoingTradeMessage: INoMoreFillsObject = {
    esid: data.esid,
    executionRequestId: data.executionRequestId,
    blocks: data.legs.map((leg: { blockId: any }) => {
      return { blockId: leg.blockId };
    }),
  };

  const noMoreFillsResponse = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/NoMoreFill",
      method: "PUT",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );

  if (noMoreFillsResponse) {
    generateToastMessageTradeData({
      action: "noMoreFillsStoreAction",
      result: "success",
      ESIDPassed: data.esid,
    });
  }
  setButtonBackToActive(outgoingTradeMessage.esid);
}

export async function updateFillsStoreAction({
  data,
  getAccessTokenSilently,
}: {
  data: any;
  getAccessTokenSilently: Function;
}) {
  // Validate all fills have prices before making the API call
  const fillsMissingPrices = validateFillsHavePrices(data);

  if (fillsMissingPrices.length > 0) {
    // Set fills to flash in the store (for cell highlighting)
    tradeDataStore.getState().updateFillsToFlash(fillsMissingPrices);

    // Clear flash after animation completes (2 seconds)
    setTimeout(() => {
      tradeDataStore.getState().clearFillsToFlash();
    }, 1000);

    // Expand the row so the user can see the fills table
    tradeDataStore.getState().updateEsidsToExpand(data.esid);

    // Show error toast
    toast.error("All fills must have a price", {
      position: "bottom-right",
    });

    // Re-enable the button and return early without making the API call
    setButtonBackToActive(data.esid);
    return;
  }

  // LQC-590 / LQC-889 parity for fills: $p and $fr fills cannot be zero or negative.
  // Zero is checked first — "-0" / "-0-00" are numerically zero, not negative.
  const fillsAtZero = validateFillsNotZeroOnPriceNotation(data);
  if (fillsAtZero.length > 0) {
    tradeDataStore.getState().updateFillsToFlash(fillsAtZero);
    setTimeout(() => {
      tradeDataStore.getState().clearFillsToFlash();
    }, 3000);
    tradeDataStore.getState().updateEsidsToExpand(data.esid);
    generateToastMessageTradeData({
      action: "fillPriceAtZero",
      result: "error",
      ESIDPassed: data.esid,
    });
    setButtonBackToActive(data.esid);
    return;
  }

  const fillsNegative = validateFillsNotNegativeOnPriceNotation(data);
  if (fillsNegative.length > 0) {
    tradeDataStore.getState().updateFillsToFlash(fillsNegative);
    setTimeout(() => {
      tradeDataStore.getState().clearFillsToFlash();
    }, 3000);
    tradeDataStore.getState().updateEsidsToExpand(data.esid);
    generateToastMessageTradeData({
      action: "fillPriceNegative",
      result: "error",
      ESIDPassed: data.esid,
    });
    setButtonBackToActive(data.esid);
    return;
  }

  // Clear any previous flash state
  tradeDataStore.getState().clearFillsToFlash();

  const blocks = buildBlocksPayload(data);

  // Validate that at least one block has fills
  const hasAnyFills = blocks.some((block) => block.fills.length > 0);

  if (!hasAnyFills) {
    generateToastMessageTradeData({
      action: "noFillsOnAnyBlock",
      result: "error",
      ESIDPassed: data.esid,
    });
    setButtonBackToActive(data.esid);
    return;
  }

  const outgoingTradeMessage: IUpdateFillsObject = {
    esid: data.esid,
    executionRequestId: data.executionRequestId,
    blocks: blocks,
  };

  const updateFillsResponse = await fetchRequestWrapper(
    {
      url: "/Trade/Execution/Fill",
      method: "PUT",
      objToSend: outgoingTradeMessage,
    },
    getAccessTokenSilently,
  );

  if (updateFillsResponse) {
    generateToastMessageTradeData({
      action: "updateFillsStoreAction",
      result: "success",
      ESIDPassed: data.esid,
    });
  }
  setButtonBackToActive(outgoingTradeMessage.esid);
}
